import RadioButton from '@/components/basicComponents/radioButton';
import { ErrorText } from '@/components/basicComponents/input';
import Label from '@/components/basicComponents/label';
import { buildTestId } from '@/lib/utils/testIds';
import { useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import { IRenderTemplateRow } from '../types';
import { evaluateDependencyItem, shouldRenderEmptyComponent } from '../utils';

interface RadioComponentProps {
  row: IRenderTemplateRow;
  pageName?: string;
  ignoreRequired: boolean;
}

const getSelectedOption = (value: any, items: any): string => {
  if (value === undefined) return '';
  return items.find((item: any) => item.value === value)?.label || '';
};

export const RadioComponent = ({ row, pageName = '', ignoreRequired }: RadioComponentProps) => {
  const { name, label, config, dependsOn, defaultValue, validation } = row;
  const items = config?.items || [];
  const { control, unregister, setValue, watch } = useFormContext();
  const dependentValue = dependsOn ? watch(`${pageName}.${dependsOn.name}`) : null;
  const hasValidOrDependency: boolean = dependsOn?.or
    ? dependsOn.or.some((item: any) => {
        const dependentValue = watch(`${pageName}.${item.name}`);
        return evaluateDependencyItem(dependentValue, item);
      })
    : null;
  const hasValidAndDependency: boolean = dependsOn?.and
    ? dependsOn.and.every((item: any) => {
        const dependentValue = watch(`${pageName}.${item.name}`);
        return evaluateDependencyItem(dependentValue, item);
      })
    : null;

  useEffect(() => {
    if (shouldRenderEmptyComponent(dependsOn, dependentValue, hasValidOrDependency, hasValidAndDependency)) {
      unregister(`${pageName}.${name}`);
    } else {
      if ((watch(`${pageName}.${name}`) === undefined && defaultValue !== undefined) || watch(`${pageName}.${name}`) === defaultValue) {
        setValue(`${pageName}.${name}`, defaultValue);
      }
    }
  }, [dependentValue, hasValidOrDependency, hasValidAndDependency]);

  if (shouldRenderEmptyComponent(dependsOn, dependentValue, hasValidOrDependency, hasValidAndDependency)) return <></>;

  return (
    <div>
      {label && (
        <Label size="md" required={!!validation?.required && !ignoreRequired}>
          {label}
        </Label>
      )}
      <Controller
        name={`${pageName}.${name}`}
        control={control}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <>
            <RadioButtonsWrapper>
              {items.map((item: any, index: number) => (
                <RadioButton
                  key={item + index}
                  name={name}
                  radioButtonText={item.label}
                  selectedOption={getSelectedOption(value, items)}
                  handleRadioChange={(event) => {
                    onChange(items.find((item: any) => item.label === event.target.value)?.value);
                  }}
                  data-testid={item.label && name ? buildTestId('signature-packet', pageName, name, item.label) : undefined}
                />
              ))}
            </RadioButtonsWrapper>
            {error?.message && <ErrorText size="md">{error?.message}</ErrorText>}
          </>
        )}
      />
    </div>
  );
};

const RadioButtonsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
`;
